import React, {useState, useContext, useEffect} from 'react';
import Box from '@mui/material/Box';
import Slide from '@mui/material/Slide';
import ActionButtons from './ActionButtons';
import MultipleChoice from '../templateInputs/MultipleChoice';
import TemplateTextInput from '../templateInputs/TemplateTextInput';
import {userState} from '../../../recoil/user/userAtom';
import {NewProjectContext} from '../utils/NewProjectContext';
import {securedFetchRequest} from '@bidmii/common/lib/util/FetchRequest';
import {API_URL} from '../../../constants';
import {useRecoilValue} from 'recoil';
import * as Sentry from '@sentry/react';
import TertiaryButton from '../../../common/StyledComponents/TertiaryButton';

interface IProps {
  changeStepHandler: (number: number) => void;
  forward: boolean;
  templateQuestion: any;
  flagBadPrompt?: () => void;
}

const DynamicInput = ({
  changeStepHandler,
  forward,
  templateQuestion,
}: IProps) => {
  const user = useRecoilValue(userState);
  const [loading, setLoading] = useState(false);
  const {project, setProject} = useContext(NewProjectContext);

  const savedDetail = project.details.find(
    (detail: $TSFixMe) => detail.prompt === templateQuestion.prompt,
  );

  const [value, setValue] = useState('');
  const [otherValue, setOtherValue] = useState(null);

  useEffect(() => {
    if (savedDetail) {
      setValue(savedDetail.value);

      const parsedOptions = JSON.parse(templateQuestion.options);
      if (
        savedDetail.type === 'MULTIPLE_CHOICE' &&
        !parsedOptions?.find(
          (option: any) => option.value === savedDetail.value,
        )
      ) {
        setValue('Other');
        setOtherValue(savedDetail.value);
      }
    }
  }, [savedDetail, templateQuestion.options]);

  useEffect(() => {
    if (
      value &&
      value !== 'Other' &&
      savedDetail?.value !== value &&
      ['MULTIPLE_CHOICE', 'BOOLEAN'].includes(templateQuestion.type)
    ) {
      handleClickNext();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const handleClickNext = () => {
    if (!value.length) {
      changeStepHandler(1);
      return;
    }

    setLoading(true);
    const body = {
      value: otherValue || value,
      type: templateQuestion.type,
      prompt: templateQuestion.prompt,
    };

    if (!savedDetail) {
      securedFetchRequest(`${API_URL}/rfps/${project.uid}/details`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      })
        .then((res) => res.json())
        .then((res) => {
          setLoading(false);
          localStorage.setItem('rfp', JSON.stringify(res));
          setProject(res);
          changeStepHandler(1);
        })
        .catch((err) => {
          setLoading(false);
          process.env.NODE_ENV === 'development' && console.error(err);

          Sentry.withScope(function (scope) {
            scope.setUser({
              uid: user?.uid,
              firstName: user?.firstName,
            });
            scope.setTag('section', 'create_user_step_2');
            Sentry.captureException(err);
          });
        });
    } else {
      securedFetchRequest(`${API_URL}/rfps/details/${savedDetail.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      })
        .then((res) => res.json())
        .then((res) => {
          setLoading(false);
          localStorage.setItem('rfp', JSON.stringify(res));
          setProject(res);
          changeStepHandler(1);
        })
        .catch((err) => {
          setLoading(false);
          process.env.NODE_ENV === 'development' && console.error(err);

          Sentry.withScope(function (scope) {
            scope.setUser({
              uid: user?.uid,
              firstName: user?.firstName,
            });
            scope.setTag('section', 'create_user_step_2');
            Sentry.captureException(err);
          });
        });
    }
  };

  const handleKeyDown = (event: $TSFixMe) => {
    if (event.keyCode === 13) {
      event.preventDefault(); // Ensure it is only this code that runs
      handleClickNext();
    }
  };

  const renderInput = () => {
    switch (templateQuestion.type) {
      case 'SHORT_FORM_TEXT':
        return (
          <TemplateTextInput
            key={templateQuestion.uid}
            state={value}
            setState={setValue}
            label={templateQuestion.prompt}
            placeholder={templateQuestion.placeholder}
            handleKeyDown={handleKeyDown}
          />
        );
      case 'LONG_FORM_TEXT':
        return (
          <TemplateTextInput
            key={templateQuestion.uid}
            state={value}
            setState={setValue}
            label={templateQuestion.prompt}
            placeholder={templateQuestion.placeholder}
            multiline
            handleKeyDown={handleKeyDown}
          />
        );
      case 'BOOLEAN':
        return (
          <MultipleChoice
            key={templateQuestion.uid}
            value={value}
            setValue={setValue}
            label={templateQuestion.prompt}
            options={[
              {label: 'Yes', value: 'Yes'},
              {label: 'No', value: 'No'},
            ]}
          />
        );
      case 'MULTIPLE_CHOICE':
        return (
          <MultipleChoice
            key={templateQuestion.uid}
            value={value}
            setValue={setValue}
            label={templateQuestion.prompt}
            options={JSON.parse(templateQuestion.options)}
            otherValue={otherValue}
            setOtherValue={setOtherValue}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Slide
      in={true}
      direction={forward ? 'left' : 'right'}
      timeout={window.outerWidth < 600 ? 200 : 400}>
      <div style={{width: '100%', maxWidth: 450}}>
        <Box pb={1}>{renderInput()}</Box>
        <ActionButtons
          secondaryAction={() => changeStepHandler(-1)}
          primaryAction={handleClickNext}
          loading={loading}
        />
        <TertiaryButton
          style={{float: 'right'}}
          onClick={() => changeStepHandler(1)}>
          skip
        </TertiaryButton>
      </div>
    </Slide>
  );
};

export default DynamicInput;
